import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

import { i18n } from '@/lib/i18n';

const INTL_LOCALE_OVERRIDES: Record<string, string> = {
    jp: 'ja'
};

export function resolveIntlLocale(language: string | undefined): string {
    const locale = language ?? 'en';
    const baseLocale = locale.split('-')[0];

    return INTL_LOCALE_OVERRIDES[locale] ?? INTL_LOCALE_OVERRIDES[baseLocale] ?? locale;
}

/**
 * Merges conditional class names with Tailwind conflict resolution.
 */
export function cn(...inputs: ClassValue[]): string {
    return twMerge(clsx(inputs));
}

/**
 * Formats a date into a short localized string.
 */
export function formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
    const resolvedDate = typeof date === 'string' ? new Date(date) : date;
    const hasPresetStyles = Boolean(options?.dateStyle || options?.timeStyle);

    return new Intl.DateTimeFormat(
        resolveIntlLocale(i18n.resolvedLanguage ?? i18n.language),
        hasPresetStyles
            ? options
            : {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  ...options
              }
    ).format(resolvedDate);
}

export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
    return new Intl.NumberFormat(resolveIntlLocale(i18n.resolvedLanguage ?? i18n.language), options).format(value);
}

/**
 * Formats a date as a relative label for recent activity timelines.
 */
export function formatRelativeTime(date: Date | string): string {
    const resolvedDate = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - resolvedDate.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);

    if (diffSec < 60) {
        return i18n.t('common.justNow');
    }

    if (diffMin < 60) {
        return i18n.t('common.minutesAgo', { count: diffMin });
    }

    if (diffHr < 24) {
        return i18n.t('common.hoursAgo', { count: diffHr });
    }

    if (diffDay < 7) {
        return i18n.t('common.daysAgo', { count: diffDay });
    }

    return formatDate(resolvedDate);
}

/**
 * Resolves after the provided number of milliseconds.
 */
export function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Generates a short non-cryptographic identifier.
 */
export function generateId(): string {
    return Math.random().toString(36).substring(2, 9);
}
